import { useCallback, useEffect, useState } from 'react'
import { ApiError, api, type Member } from '../api'
import { Card, Confirm, CopyButton, Loading, Modal, expiresIn, shortDate } from '../components'
import { vocabulary } from '../vocabulary'

const t = vocabulary.team
const c = vocabulary.common

/**
 * Who can do what, and how someone new gets in. The three permissions are described by the thing
 * they unlock, never by the name they are stored under.
 */
const PERMISSIONS = vocabulary.permissions

function describe(permissions: string[]): string[] {
  return PERMISSIONS.filter((p) => permissions.includes(p.slug)).map((p) => p.label)
}

export default function Team({ accountId, canManage }: {
  accountId: string
  canManage: boolean
}) {
  const [members, setMembers] = useState<Member[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<Member | null>(null)
  const [removing, setRemoving] = useState<Member | null>(null)
  const [inviting, setInviting] = useState(false)
  const [chosen, setChosen] = useState<string[]>(['delegator-admin'])
  const [invite, setInvite] = useState<{ token: string; expiresAt: string } | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    let live = true
    api.team(accountId)
      .then((data) => live && setMembers(data.members))
      .catch((failure) => live && setError(
        failure instanceof ApiError ? failure.message : t.loadError))
    return () => { live = false }
  }, [accountId])

  useEffect(() => {
    setMembers(null)
    setError(null)
    return load()
  }, [load])

  function toggle(slug: string) {
    setChosen((current) =>
      current.includes(slug) ? current.filter((s) => s !== slug) : [...current, slug])
  }

  async function save() {
    if (!editing) return
    setBusy(true)
    setError(null)
    try {
      await api.changePermissions(accountId, editing.user_id, chosen)
      setEditing(null)
      load()
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : t.saveError)
    } finally {
      setBusy(false)
    }
  }

  async function confirmRemove() {
    if (!removing) return
    setBusy(true)
    setError(null)
    try {
      await api.removeMember(accountId, removing.user_id)
      setRemoving(null)
      load()
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : t.removeError)
    } finally {
      setBusy(false)
    }
  }

  async function createInvite() {
    setBusy(true)
    setError(null)
    try {
      const minted = await api.inviteTeammate(accountId, chosen)
      setInvite({ token: minted.invite_token, expiresAt: minted.expires_at })
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : t.inviteError)
    } finally {
      setBusy(false)
    }
  }

  if (error && !members) return <p className="banner stop">{error}</p>
  if (!members) return <Loading rows={4} />

  const inviteLink = invite
    ? `${window.location.origin}/join#invite=${encodeURIComponent(invite.token)}`
    : ''

  return (
    <>
      <div className="stack" style={{ gap: 3 }}>
        <h1>{t.title}</h1>
        <p className="small muted">{t.subhead}</p>
      </div>

      {error && <p className="banner stop">{error}</p>}

      <Card
        title={t.members}
        action={canManage
          ? <button
              className="btn primary small"
              onClick={() => { setChosen(['delegator-admin']); setInvite(null); setInviting(true) }}
            >
              {t.inviteTeammate}
            </button>
          : undefined}
      >
        <div className="table-wrap">
          <table className="list">
            <thead>
              <tr><th>{t.colPerson}</th><th>{t.colCan}</th><th>{t.colJoined}</th>{canManage && <th className="right"></th>}</tr>
            </thead>
            <tbody>
              {members.map((member) => (
                <tr key={member.user_id}>
                  <td className="primary" data-label={t.colPerson}>
                    <div className="row">
                      {member.picture_url
                        ? <img className="avatar" src={member.picture_url} alt="" referrerPolicy="no-referrer" />
                        : <span className="avatar">{(member.display_name ?? member.email ?? '?').charAt(0).toUpperCase()}</span>}
                      <div className="stack" style={{ gap: 0 }}>
                        <span>
                          {member.display_name ?? member.email}
                          {member.is_you && <span className="tiny faint"> {t.you}</span>}
                        </span>
                        {member.display_name && <span className="tiny faint">{member.email}</span>}
                      </div>
                    </div>
                  </td>
                  <td data-label={t.colCan} className="small muted">{describe(member.permissions).join(' · ') || '—'}</td>
                  <td data-label={t.colJoined} className="small muted">{shortDate(member.joined_at)}</td>
                  {canManage && (
                    <td className="actions right" data-label="">
                      <div className="row" style={{ justifyContent: 'flex-end' }}>
                        <button
                          className="btn small"
                          disabled={busy}
                          onClick={() => { setChosen(member.permissions); setEditing(member) }}
                        >
                          {t.change}
                        </button>
                        <button className="btn small danger" disabled={busy} onClick={() => setRemoving(member)}>
                          {t.remove}
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {editing && (
        <Modal title={`${t.editWhatPrefix} ${editing.display_name ?? editing.email} ${t.editWhatSuffix}`} onClose={() => setEditing(null)}>
          <div className="stack" style={{ gap: 14 }}>
            <PermissionPicker chosen={chosen} onToggle={toggle} />
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <button className="btn" onClick={() => setEditing(null)}>{c.cancel}</button>
              <button className="btn primary" disabled={busy || chosen.length === 0} onClick={save}>
                {c.save}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {removing && (
        <Confirm
          title={t.removeTitle}
          confirmLabel={t.removeConfirm}
          danger
          busy={busy}
          onConfirm={confirmRemove}
          onClose={() => setRemoving(null)}
          body={
            <>
              <b>{removing.display_name ?? removing.email}</b>{t.removeBody}
            </>
          }
        />
      )}

      {inviting && (
        <Modal
          title={t.inviteTitle}
          onClose={() => { setInviting(false); setInvite(null); load() }}
          footer={t.inviteFooter}
        >
          <div className="stack" style={{ gap: 14 }}>
            {!invite ? (
              <>
                <span className="label">{t.whatCanDo}</span>
                <PermissionPicker chosen={chosen} onToggle={toggle} />
                <div className="row" style={{ justifyContent: 'flex-end' }}>
                  <button
                    className="btn primary"
                    disabled={busy || chosen.length === 0}
                    onClick={createInvite}
                  >
                    {t.createInviteLink}
                  </button>
                </div>
              </>
            ) : (
              <div className="stack" style={{ gap: 10 }}>
                <span className="label">{t.sendLink}</span>
                <div className="row">
                  <code className="input mono" style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}>
                    {inviteLink}
                  </code>
                  <CopyButton value={inviteLink} label={c.copy} />
                </div>
                <span className="tiny faint">
                  {t.expiresWord} {expiresIn(invite.expiresAt)}{t.inviteExpiresSuffix}
                </span>
              </div>
            )}
          </div>
        </Modal>
      )}
    </>
  )
}

function PermissionPicker({ chosen, onToggle }: {
  chosen: string[]
  onToggle: (slug: string) => void
}) {
  return (
    <div className="stack" style={{ gap: 9 }}>
      {PERMISSIONS.map((permission) => (
        <label key={permission.slug} className="row" style={{ alignItems: 'flex-start', gap: 9 }}>
          <input
            type="checkbox"
            checked={chosen.includes(permission.slug)}
            onChange={() => onToggle(permission.slug)}
            style={{ marginTop: 3 }}
          />
          <span className="stack" style={{ gap: 1 }}>
            <span className="small">{permission.label}</span>
            <span className="tiny faint">{permission.hint}</span>
          </span>
        </label>
      ))}
    </div>
  )
}
